import {
 ChangeDetectionStrategy,
 Component,
 DestroyRef,
 ElementRef,
 EventEmitter,
 Input,
 OnInit,
 Output,
 Renderer2,
 ViewChild,
 WritableSignal,
 inject,
 signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { debounceTime, finalize, fromEvent, of, switchMap, takeUntil, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
 selector: 'iho-sliderange',
 standalone: true,
 imports: [CommonModule],
 templateUrl: './sliderange.component.html',
 styleUrls: ['./sliderange.component.scss'],
 changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderangeComponent implements OnInit {
 constructor(private renderer: Renderer2) {}

 @Input() min!: number;
 @Input() max!: number;
 @Input() _minValue!: number | string;
 @Input() _maxValue!: number | string;
 @Output() minValueChanged: EventEmitter<number> = new EventEmitter();
 @Output() maxValueChanged: EventEmitter<number> = new EventEmitter();
 @Output() maxValueChanging: EventEmitter<number> = new EventEmitter();
 @Output() minValueChanging: EventEmitter<number> = new EventEmitter();
 @Input() set reset(value: boolean) {
  if (value != undefined) this.resetSlideRange();
 }

 @ViewChild('rangePin1', { static: true }) rangePin1!: ElementRef;
 @ViewChild('rangePin2', { static: true }) rangePin2!: ElementRef;
 @ViewChild('tracker', { static: true }) tracker!: ElementRef;

 destroyRef = inject(DestroyRef);

 minPinX = signal<number>(0);
 maxPinX = signal<number>(238);
 rangeMinValue = signal(this.min);
 rangeMaxValue = signal(this.max);
 trackerWidth!: number;
 overlapping = false;

 rate: any;

 resetSlideRange() {
  // this.minPinX.set(0);
  // this.maxPinX.set(+this.tracker.nativeElement.clientWidth);
  // this.updatePinPosition(this.minPinX(), this.tracker.nativeElement, this.rangePin1.nativeElement);
  // this.updatePinPosition(this.maxPinX(), this.tracker.nativeElement, this.rangePin2.nativeElement);
  // this.rangeMinValue.set(this.min)
  // this.rangeMaxValue.set(this.max)
  // this.minValueChanged.emit(+this.rangeMinValue());
  // this.minValueChanged.emit(+this.rangeMaxValue());
  // this.minEmit()
  // this.maxEmit()
 }

 ngOnInit(): void {
  this.trackerWidth = +this.tracker.nativeElement.clientWidth;
  this.maxPinX.set(+this.tracker.nativeElement.clientWidth);

  if (this._minValue) {
   this.minPinX.set(
    this._minValue == this.min
     ? 0
     : +this._minValue / +(+this.max / +this.tracker.nativeElement.clientWidth)
   );
   this.updatePinPosition(
    this.minPinX(),
    this.tracker.nativeElement,
    this.rangePin2.nativeElement,
    true
   );
   this.rangeMinValue.set(+this._minValue);

   this.minValueChanging.emit(+this.rangeMinValue());
   // this.minValueChanged.emit(+this.rangeMinValue())
  }
  if (this._maxValue) {
   this.maxPinX.set(+this._maxValue / +(+this.max / +this.tracker.nativeElement.clientWidth));
   this.updatePinPosition(
    this.maxPinX(),
    this.tracker.nativeElement,
    this.rangePin1.nativeElement,
    true
   );

   this.rangeMaxValue.set(+this._maxValue);
   this.maxValueChanging.emit(+this.rangeMaxValue());
   // this.maxValueChanged.emit(+this.rangeMaxValue())
  }
  this.setupPinMovement(this.rangePin2.nativeElement, 2);
  this.setupPinMovement(this.rangePin1.nativeElement, 1);
 }

 setupPinMovement(pinElement: HTMLElement, pinNumber: number): void {
  fromEvent(pinElement, 'touchstart')
   .pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap((touchStartEvent) => {
     touchStartEvent.preventDefault();
     return fromEvent(document, 'touchmove').pipe(
      takeUntil(fromEvent(document, 'touchend')),
      tap((touchMoveEvent) => this.handleMoveEvent(touchMoveEvent, pinElement, pinNumber)),
      finalize(() => {
       if (pinNumber == 2) this.minEmit();
       else this.maxEmit();
      })
     );
    })
   )
   .subscribe();

  fromEvent(pinElement, 'mousedown')
   .pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap((_) => {
     return fromEvent(document, 'mousemove').pipe(
      takeUntil(fromEvent(document, 'mouseup')),
      tap((mouseMoveEvent) => {
       this.handleMoveEvent(mouseMoveEvent, pinElement, pinNumber);
      }),
      finalize(() => {
       if (pinNumber == 2) this.minEmit();
       else this.maxEmit();
      })
     );
    })
   )
   .subscribe();
 }

 private handleMoveEvent(event: Event, pinElement: HTMLElement, pinNumber: number): void {
  const clientX =
   'touches' in event ? (event as TouchEvent).touches[0].clientX : (event as MouseEvent).clientX;
  this.movePin(clientX, pinElement, pinNumber);
 }

 movePin(pinClientX: number, pinEl: HTMLElement, pinNum: number): void {
  const trackerRect = this.tracker.nativeElement.getBoundingClientRect();

  if (pinClientX < trackerRect.left || pinClientX > trackerRect.right) {
   return;
  }

  const pinOffset = trackerRect.right - pinClientX;

  if (pinNum === 2) this.minPinX.set(pinOffset);
  else this.maxPinX.set(pinOffset);

  this.updatePinPosition(pinOffset, this.tracker.nativeElement, pinEl);
  if (pinNum == 2) {
   if (+this.rangeMinValue() - this.min < this.rate) {
    this.minValueChanging.emit(+this.min);
   } else if (+this.rangeMinValue() < 5000000 && this.rangeMinValue() > this.min)
    this.minValueChanging.emit(5000000);
   else if (this.min == this.max) {
    this.minValueChanging.emit(this.min);
    return;
   } else {
    this.minValueChanging.emit(this.roundRangeValueEmit(+this.rangeMinValue()));
   }
  } else {
   if (this.max - +this.rangeMaxValue() < 5000000) this.maxValueChanging.emit(+this.max);
   else if (
    this.rangeMaxValue() < this.max &&
    this.rangeMaxValue() > Math.floor(this.max / 5000000) * 5000000
   )
    this.maxValueChanging.emit(Math.floor(this.max / 5000000) * 5000000);
   else if (this.min == this.max) {
    this.minValueChanging.emit(this.max);
    return;
   } else this.maxValueChanging.emit(this.roundRangeValueEmit(+this.rangeMaxValue()));
  }
 }

 roundRangeValueEmit(value: number) {
  return Math.round(value / 5000000) * 5000000;
 }

 updatePinPosition(
  newPinPosition: number,
  tracker: HTMLElement,
  pinEl: HTMLElement,
  readQuery = false
 ): void {
  const overlapping = this.maxPinX() - this.minPinX() < 25;

  this.overlapping = overlapping;

  if (
   !overlapping &&
   newPinPosition >= 0 &&
   newPinPosition <= this.tracker.nativeElement.clientWidth
  ) {
   // if (
   //   +this.rangeMaxValue() - +this.rangeMinValue() < 0 ||
   //   +this.rangeMaxValue() - +this.min < 0
   // )
   //   return;

   const adjustedPosition = newPinPosition - pinEl.clientWidth / 2;
   this.renderer.setStyle(pinEl, 'right', `${adjustedPosition}px`);
   this.fillColor();
   if (!readQuery && this.min !== this.max) this.updateRange();
  }
 }

 updateRange() {
  const rate = this.max / this.tracker.nativeElement.clientWidth;
  this.rate = rate;
  this.rangeMinValue.set(this.minPinX() * rate);
  // if (+this.rangeMinValue() - this.min < this.rate)
  // this.rangeMinValue.set(this.min);
  this.rangeMaxValue.set(this.maxPinX() * rate);
 }

 fillColor() {
  const percent1 = (this.minPinX() / this.tracker.nativeElement.clientWidth) * 100;
  const percent2 = (this.maxPinX() / this.tracker.nativeElement.clientWidth) * 100;
  this.renderer.setStyle(
   this.tracker.nativeElement,
   'background',
   `linear-gradient(to left, #D0D5DD ${percent1}% , #175CD3 ${percent1}% , #175CD3 ${percent2}%, #D0D5DD ${percent2}%)`
  );
 }

 minEmit() {
  if (+this.rangeMinValue() - this.min < this.rate) this.minValueChanged.emit(+this.min);
  else this.minValueChanged.emit(+this.rangeMinValue());
 }

 maxEmit() {
  if (this.max - +this.rangeMaxValue() < this.rate) this.maxValueChanged.emit(+this.max);
  else this.maxValueChanged.emit(+this.rangeMaxValue());
 }
}
